import React from 'react';
import {
    Alert,
    Linking,
    Platform,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from 'react-native';
import { useRouter } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import colors from '../../lib/colors';
import { useFontLoading } from '../../lib/FontContext';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { FeedPaper } from '../../lib/hooks/useProjectFeed';
import {
    DownvoteReason,
    FeedbackVote,
} from '../../lib/projectPaperFeedback';
import { getVersionedUrl } from '../../lib/paperUrl';
import { getPaperVenueLabel } from '../../lib/sourceHelpers';
import PaperCliPromo from '../papers/PaperCliPromo';
import ProjectFeedFeedbackControl from './ProjectFeedFeedbackControl';
import {
    buildDetailMetadata,
    formatAuthors,
    getAlphaXivOverviewUrl,
} from './projectFeedUi';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { resolveMessage } from '../../lib/i18n/messages';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

interface ProjectFeedDetailContentProps {
    item: FeedPaper;
    paperLinkHref: string;
    feedbackPending: boolean;
    onSetFeedback: (vote: FeedbackVote, reason?: DownvoteReason) => Promise<void>;
    onClearFeedback: () => Promise<void>;
    feedbackButtonAlignment?: 'center' | 'left';
}

function appendQueryParam(href: string, key: string, value: string) {
    const [pathname, search = ''] = href.split('?');
    const searchParams = new URLSearchParams(search);
    searchParams.set(key, value);
    const nextSearch = searchParams.toString();

    return nextSearch ? `${pathname}?${nextSearch}` : pathname;
}

async function openExternalUrl(url: string | null, errorTitle: string, errorMessage: string) {
    if (!url) {
        return;
    }

    try {
        const supported = await Linking.canOpenURL(url);
        if (supported) {
            await Linking.openURL(url);
            return;
        }

        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.open(url, '_blank');
            return;
        }
    } catch {
        // Fall through to alert below.
    }

    Alert.alert(errorTitle, errorMessage);
}

function renderMatchChips(
    values: string[] | undefined,
    fontsLoaded: boolean
) {
    if (!values || values.length === 0) {
        return null;
    }

    return (
        <View style={styles.matchChipRow}>
            {values.map((value) => (
                <View key={value} style={styles.matchChip}>
                    <Text style={[styles.matchChipText, fontsLoaded && interSemiBold]}>
                        {value}
                    </Text>
                </View>
            ))}
        </View>
    );
}

export default function ProjectFeedDetailContent({
    item,
    paperLinkHref,
    feedbackPending,
    onSetFeedback,
    onClearFeedback,
    feedbackButtonAlignment = 'center',
}: ProjectFeedDetailContentProps) {
    const { fontsLoaded } = useFontLoading();
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();
    const router = useRouter();
    const metadata = buildDetailMetadata(item, formatLocale)
        .map((value) => resolveMessage(i18n, value))
        .join(' • ');
    const pdfUrl = getVersionedUrl(item.paper.pdf_url, item.paper.source_paper_version);
    const sourceUrl = getVersionedUrl(item.paper.url, item.paper.source_paper_version);
    const alphaXivUrl = getAlphaXivOverviewUrl(
        item.paper.source_id,
        item.paper.source_paper_id,
        item.paper.url
    );
    const venueLabel = item.paper.venue_name || getPaperVenueLabel({
        sourceId: item.paper.source_id,
        sourcePaperId: item.paper.source_paper_id,
        doi: item.paper.doi,
        url: item.paper.url,
    });
    const matchingDetails = item.matching_details || {};
    const hasMatchContext = Boolean(
        matchingDetails.explanation
        || matchingDetails.matchedKeywords?.length
        || matchingDetails.matchedCategories?.length
    );
    const shareLinkHref = appendQueryParam(paperLinkHref, 'share', '1');
    const openPaperSummary = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.open(paperLinkHref, '_blank', 'noopener,noreferrer');
            return;
        }

        router.push(paperLinkHref);
    };
    const openPaperShare = () => {
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.open(shareLinkHref, '_blank', 'noopener,noreferrer');
            return;
        }

        router.push(shareLinkHref);
    };

    return (
        <View style={styles.container} testID="project-feed-detail-content">
            <View style={styles.headerSection}>
                <TouchableOpacity
                    style={styles.titleLink}
                    activeOpacity={0.75}
                    onPress={openPaperSummary}
                    testID="project-feed-title-link"
                >
                    <Text style={[styles.title, fontsLoaded && interSemiBold]}>
                        {item.paper.title}
                    </Text>
                    <FontAwesome6
                        name="arrow-up-right-from-square"
                        size={18}
                        color={colors.text500}
                        style={styles.titleIcon}
                    />
                </TouchableOpacity>
                <Text style={[styles.authors, fontsLoaded && interRegular]}>
                    {formatAuthors(item.paper.authors)}
                </Text>
                {metadata ? (
                    <Text style={[styles.metadata, fontsLoaded && interRegular]}>
                        {metadata}
                    </Text>
                ) : null}
            </View>

            <ProjectFeedFeedbackControl
                key={item.id}
                feedback={item.project_paper_feedback}
                pending={feedbackPending}
                onSetFeedback={onSetFeedback}
                onClearFeedback={onClearFeedback}
                buttonAlignment={feedbackButtonAlignment}
            />

            <View style={styles.section}>
                <Text style={[styles.sectionLabel, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.abstract" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                    Abstract
                </Trans></Text>
                <Text style={[styles.sectionBody, fontsLoaded && interRegular]}>
                    {item.paper.abstract || <Trans id="feed.projectfeeddetailcontent.abstractNotAvailable" comment="Conditional UI copy in components/feed/ProjectFeedDetailContent.tsx">Abstract not available.</Trans>}
                </Text>
            </View>

            {hasMatchContext ? (
                <View style={styles.section}>
                    <Text style={[styles.sectionLabel, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.whyItMatched" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                        Why it matched
                    </Trans></Text>
                    {matchingDetails.explanation ? (
                        <Text style={[styles.sectionBody, fontsLoaded && interRegular]}>
                            {matchingDetails.explanation}
                        </Text>
                    ) : null}
                    {renderMatchChips(matchingDetails.matchedKeywords, fontsLoaded)}
                    {renderMatchChips(matchingDetails.matchedCategories, fontsLoaded)}
                </View>
            ) : null}

            {item.personalized_note ? (
                <View style={styles.section}>
                    <Text style={[styles.sectionLabel, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.note" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                        Note
                    </Trans></Text>
                    <Text style={[styles.sectionBody, fontsLoaded && interRegular]}>
                        {item.personalized_note}
                    </Text>
                </View>
            ) : null}

            <View style={styles.section}>
                <Text style={[styles.sectionLabel, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.actions" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                    Actions
                </Trans></Text>
                <View style={styles.actionRow}>
                    {pdfUrl ? (
                        <TouchableOpacity
                            style={styles.actionButton}
                            onPress={() => { void openExternalUrl(
                                pdfUrl,
                                i18n._(msg({ id: 'common.couldNotOpenLink', message: 'Could not open link' })),
                                i18n._(msg({ id: 'common.pleaseTryAgain', message: 'Please try again.' }))
                            ); }}
                            activeOpacity={0.75}
                        >
                            <FontAwesome6 name="file-pdf" size={13} color={colors.text700} />
                            <Text style={[styles.actionText, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.viewPdf" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                                View PDF
                            </Trans></Text>
                        </TouchableOpacity>
                    ) : null}

                    {sourceUrl ? (
                        <TouchableOpacity
                            style={styles.actionButton}
                            onPress={() => { void openExternalUrl(
                                sourceUrl,
                                i18n._(msg({ id: 'common.couldNotOpenLink', message: 'Could not open link' })),
                                i18n._(msg({ id: 'common.pleaseTryAgain', message: 'Please try again.' }))
                            ); }}
                            activeOpacity={0.75}
                        >
                            <FontAwesome6 name="arrow-up-right-from-square" size={12} color={colors.text700} />
                            <Text style={[styles.actionText, fontsLoaded && interSemiBold]}>
                                {venueLabel
                                    ? i18n._({ ...msg({ id: 'feed.viewOnSource', message: 'View on {source}' }), values: { source: venueLabel } })
                                    : <Trans id="feed.projectfeeddetailcontent.openSourcePage" comment="Conditional UI copy in components/feed/ProjectFeedDetailContent.tsx">Open source page</Trans>}
                            </Text>
                        </TouchableOpacity>
                    ) : null}

                    <TouchableOpacity
                        style={styles.actionButton}
                        activeOpacity={0.75}
                        onPress={openPaperSummary}
                        testID="project-feed-ai-summary-link"
                    >
                        <FontAwesome6 name="robot" size={12} color={colors.text700} />
                        <Text style={[styles.actionText, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.aiSummary" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                            AI summary
                        </Trans></Text>
                    </TouchableOpacity>

                    {alphaXivUrl ? (
                        <TouchableOpacity
                            style={styles.actionButton}
                            activeOpacity={0.75}
                            onPress={() => { void openExternalUrl(
                                alphaXivUrl,
                                i18n._(msg({ id: 'common.couldNotOpenLink', message: 'Could not open link' })),
                                i18n._(msg({ id: 'common.pleaseTryAgain', message: 'Please try again.' }))
                            ); }}
                            testID="project-feed-alphaxiv-link"
                        >
                            <FontAwesome6 name="arrow-up-right-from-square" size={12} color={colors.text700} />
                            <Text style={[styles.actionText, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.openInAlphaxiv" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                                Open in alphaXiv
                            </Trans></Text>
                        </TouchableOpacity>
                    ) : null}

                    <TouchableOpacity
                        style={styles.actionButton}
                        activeOpacity={0.75}
                        onPress={openPaperShare}
                        testID="project-feed-share-link"
                    >
                        <FontAwesome6 name="share-nodes" size={12} color={colors.text700} />
                        <Text style={[styles.actionText, fontsLoaded && interSemiBold]}><Trans id="feed.projectfeeddetailcontent.share" comment="UI copy in components/feed/ProjectFeedDetailContent.tsx">
                            Share
                        </Trans></Text>
                    </TouchableOpacity>
                </View>

                <PaperCliPromo
                    paper={item.paper}
                    recommendationTargetId={item.short_id || item.id}
                    variant="inline"
                />
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        gap: 24,
        padding: 24,
    },
    headerSection: {
        gap: 10,
    },
    titleLink: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 10,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    title: {
        flex: 1,
        fontSize: 24,
        lineHeight: 31,
        color: colors.text900,
    },
    titleIcon: {
        marginTop: 5,
    },
    authors: {
        fontSize: 15,
        lineHeight: 22,
        color: colors.text600,
    },
    metadata: {
        fontSize: 13,
        lineHeight: 19,
        color: colors.text500,
    },
    section: {
        gap: 12,
    },
    sectionLabel: {
        fontSize: 12,
        color: colors.text500,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
    },
    sectionBody: {
        fontSize: 15,
        lineHeight: 25,
        color: colors.text700,
    },
    matchChipRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
    },
    matchChip: {
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#dde2ea',
        backgroundColor: '#fafbfd',
        paddingHorizontal: 10,
        paddingVertical: 6,
    },
    matchChipText: {
        fontSize: 12,
        color: colors.text700,
    },
    actionRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    actionButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: '#d8dde6',
        backgroundColor: '#ffffff',
        paddingHorizontal: 14,
        paddingVertical: 10,
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    actionText: {
        fontSize: 13,
        color: colors.text700,
    },
});
